import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Tabs, Tab, Button, Box, Pagination } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { Sidebar } from 'primereact/sidebar';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { toast } from 'react-toastify';
import { FaFilter } from 'react-icons/fa';
import { TiExport } from 'react-icons/ti';

import OurLayouts from '../Layouts';
import apiService from '../../services/apiservice';
import AddEnquiry from './addenquiry';
import AssignEnquiry from './assign';
import AddService from './addService';
import FilterEnquiry from './filterEnquiry';
import ServiceEnquiry from './serviceEnquriyform';
import { useGlobalContext } from '../../GlobalContext';

import { AllTabTable, NewTabTable, AssignTabTable } from './EnquiryTables';
import BillingView from './BillingView';
import { useEnquiryUtils } from './useEnquiryUtils';
import { ROLE_CODES, STATUS_LIST } from './enquiryConstants';

const SIDEBAR_HEADER_LABELS = {
  View: 'View Service Enquiry Details',
  Assign: 'Assign Enquiry',
  Filter: 'Filter Enquiry',
  Add: 'Add Service',
};

const mergeUnique = (prev, newItems) =>
  [...prev, ...newItems.filter((newItem) => !prev.some((existing) => existing.id === newItem.id))];

const EnquiryList = () => {
  const userObj = JSON.parse(localStorage.getItem('loginObj'));
  const roleCode = localStorage.getItem('userRole');
  const user = userObj.user;
  const { exporting, setExporting } = useGlobalContext();
  const childRef = useRef();

  const [loading, setLoading] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [viewEnquiry, setViewEnquiry] = useState(false);
  const [paginatedEnquiryDetails, setPaginatedEnquiryDetails] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRow, setSelectedRow] = useState({});
  const [exportObj, setExportObj] = useState({});
  const [viewServiceEnquiry, setViewServiceEnquiry] = useState(false);
  const [enquiryId, setEnquiryId] = useState(null);
  const [mode, setMode] = useState('');
  const [userType, setUserType] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(1);
  const [billingView, setBillingView] = useState(false);
  const [billingData, setBillingData] = useState([]);
  const [billingMode, setBillingMode] = useState('');

  const {
    convertUTCToLocal,
    formatDate,
    getStatusStyle,
    getStatusLabel,
    getConciergeStyle,
    getBillingStyle,
    getBillingStatusText,
    getConciergeStatusText,
    renderTraumaStatus,
  } = useEnquiryUtils();

  const shouldShowAssignTab = useMemo(
    () => roleCode === ROLE_CODES.CE || roleCode === ROLE_CODES.GROUPPRADMIN,
    [roleCode]
  );

  const shouldShowStatusFilter = useMemo(() => activeTab !== 0, [activeTab]);

  const handleTabChange = useCallback((_, newValue) => {
    setFilterObj({});
    setPage(1);
    setPaginatedEnquiryDetails([]);
    setActiveTab(newValue);
  }, []);

  const handlePageChange = useCallback((_, value) => {
    setPage(value);
    setPaginatedEnquiryDetails([]);
  }, []);

  const handleFilterChange = useCallback((e) => {
    setFilterObj((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const clearFilter = useCallback(() => {
    setViewEnquiry(false);
    setFilterObj({});
  }, []);

  const filterCancel = useCallback((filter) => {
    setPage(1);
    setViewEnquiry(false);
    setFilterObj(filter);
  }, []);

  const handleCancelView = useCallback(() => {
    if (childRef.current) {
      const childValue = childRef.current.getValue();
      if (childValue.billingStatus === 0 && childValue.amount) {
        toast.warn('Please get Approval from Admin');
        return;
      }
    }
    setViewEnquiry(false);
  }, []);

  const openEnquiryPanel = useCallback(
    (data, panelMode) => {
      const isBlocked =
        (data.billingStatus === 2 && panelMode === 'Add') ||
        (panelMode === 'Add' && roleCode === ROLE_CODES.ACCADMIN);
      if (isBlocked) return;
      setMode(panelMode);
      setSelectedRow(data);
      setViewEnquiry(true);
    },
    [roleCode]
  );

  const openAssignPanel = useCallback(
    (data, panelMode, type) => {
      const isBlocked =
        (data.billingStatus === 2 && panelMode === 'Add') ||
        (panelMode === 'Add' && roleCode === ROLE_CODES.ACCADMIN);
      if (isBlocked) return;
      setMode(panelMode);
      setUserType(type);
      setSelectedRow(data);
      setViewEnquiry(true);
    },
    [roleCode]
  );

  const exportToExcel = useCallback(async () => {
    setExporting(true);
    const payload = { ...exportObj };
    if (!payload.fromDate && !payload.fromDate1) {
      const first = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const last = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
      payload.fromDate = formatDate(first);
      payload.toDate = formatDate(last);
      setFilterObj((prev) => ({ ...prev, fromDate: payload.fromDate, toDate: payload.toDate }));
    }
    ['nameSearch', 'patientId', 'patientName'].forEach((key) => {
      if (!payload[key]) delete payload[key];
    });
    await apiService.exportNewEenqList(payload);
    setExporting(false);
  }, [exportObj, setExporting, formatDate]);

  const onBillingView = useCallback(
    async (data) => {
      const payload = { serviceEnquiryId: data?.id || selectedRow.id, allBillingFlag: 1 };
      const res = await apiService.getServicesBillList(payload);
      if (res.data && res.data.length > 0) {
        setBillingData(res.data.successList || []);
        if (data?.id) {
          setBillingView(true);
          setSelectedRow(data);
        }
        setBillingMode('Edit');
      } else {
        toast('No data found');
      }
    },
    [selectedRow.id]
  );

  const buildEnqPayload = useCallback(() => {
    const payload = { ...filterObj, pageNumber: page, tab: activeTab === 0 ? 0 : 1 };

    if (activeTab === 0) {
      payload.status = 11;
    } else if (activeTab === 1) {
      if (roleCode === ROLE_CODES.CE) {
        payload.conciergeUserCode = user.code;
      } else {
        payload.assignedUser = user.code;
      }
      payload.roleCode = roleCode;
    }

    if (filterObj?.status !== undefined) {
      payload.status = filterObj.status;
    }

    ['nameSearch', 'patientId', 'patientName'].forEach((key) => {
      if (!payload[key]) delete payload[key];
    });

    return payload;
  }, [activeTab, filterObj, page, roleCode, user.code]);

  const getEnqList = useCallback(async () => {
    if (page === 1) setPaginatedEnquiryDetails([]);
    setCount(1);
    setLoading(true);
    const payload = buildEnqPayload();
    setExportObj(payload);

    try {
      const response = await apiService.getenqList(payload);
      const successList = response.data.successList || [];
      const totalRecords = response.data?.pagination?.totalRecords || 0;
      if (successList.length > 0) {
        setCount(Math.ceil(totalRecords / 10) - 1);
        setPaginatedEnquiryDetails((prev) => mergeUnique(prev, successList));
      }
    } catch (error) {
      console.error('Error fetching enquiry list:', error);
    } finally {
      setLoading(false);
    }
  }, [buildEnqPayload, page]);

  useEffect(() => {
    getEnqList();
  }, [activeTab, filterObj, page, viewEnquiry]);

  const shouldShowAssignButton = useCallback(
    (item) => item.status !== 0 && item.status !== 7,
    []
  );

  const tableProps = {
    data: paginatedEnquiryDetails,
    convertUTCToLocal,
    getStatusStyle,
    getStatusLabel,
    renderTraumaStatus,
    onView: (item) => openEnquiryPanel(item, 'View'),
    onBillingView,
    onAddService: (item) => openEnquiryPanel(item, 'Add'),
    onAssignExecutive: (item) => openAssignPanel(item, 'Assign', 'CE'),
    onAssignBackOffice: (item) => openAssignPanel(item, 'Assign', 'BO'),
    shouldShowAssignButton,
  };

  let filterMode;
  if (activeTab === 0) {
    filterMode = 'New';
  } else if (activeTab === 1) {
    filterMode = 'Assign';
  } else {
    filterMode = 'All';
  }
  return (
    <OurLayouts>
      <Backdrop sx={{ color: '#FFFFFF', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress size={80} color="inherit" />
      </Backdrop>

      <div>
        <Sidebar
          style={{ width: '60%', borderBottom: '1px solid #ccc' }}
          dismissable={false}
          visible={viewEnquiry}
          header={
            <div className="font-poppins text-primary text-xl font-bold leading-6 tracking-normal text-left">
              {SIDEBAR_HEADER_LABELS[mode] || 'Add Service'}
            </div>
          }
          centered
          onHide={handleCancelView}
          position="right"
        >
          {mode === 'View' && (
            <div style={{ padding: '1rem' }}>
              <AddEnquiry selected={selectedRow} onClose={handleCancelView} />
            </div>
          )}
          {mode === 'Assign' && (
            <div style={{ padding: '1rem' }}>
              <AssignEnquiry selected={selectedRow} userType={userType} onClose={clearFilter} />
            </div>
          )}
          {mode === 'Add' && (
            <div style={{ padding: '1rem' }}>
              <AddService selected={selectedRow} mode={billingMode} ref={childRef} onClose={clearFilter} />
            </div>
          )}
          {mode === 'Filter' && (
            <div style={{ padding: '1rem' }}>
              <FilterEnquiry
                onCance={clearFilter}
                filterObj={filterObj}
                mode={filterMode}
                onClear={() => setFilterObj({})}
                onClose={filterCancel}
              />
            </div>
          )}
        </Sidebar>

        <Sidebar
          visible={viewServiceEnquiry}
          onHide={() => setViewServiceEnquiry(false)}
          style={{ width: '60%' }}
          position="right"
        >
          <ServiceEnquiry enquiryId={enquiryId} />
        </Sidebar>
      </div>

      <div className="flex flex-col w-full mt-[7rem] overflow-auto">
        <div className="flex justify-between">
          <h1 className="text-2xl text-primary font-semibold mb-8">Service Enquiry List</h1>
          <div className="flex border h-10 rounded-md pt-1 pb-1 mr-12 justify-items-center">
            <div className="flex mx-2 border-r-2">
              <Button className="flex gap-1" onClick={() => openEnquiryPanel({}, 'Filter')}>
                <FaFilter className="text-primary" />
                <h6 className="text-primary">Filter</h6>
              </Button>
            </div>
            <div className="flex mx-2 border-r-2">
              <Button className="flex gap-1" onClick={exportToExcel} disabled={exporting}>
                <TiExport className="text-primary text-2xl" />
                <h6 style={{ color: exporting ? 'green' : '#004172' }}>
                  {exporting ? 'Downloading...' : 'Export Excel'}
                </h6>
              </Button>
              {exporting && (
                <ProgressSpinner
                  style={{ width: '20px', height: '20px' }}
                  strokeWidth="8"
                  fill="var(--surface-ground)"
                  animationDuration=".5s"
                />
              )}
            </div>
          </div>
        </div>

        {shouldShowStatusFilter && (
          <div className="flex items-center mt-4 gap-2 mr-10">
            <div className="ml-3 flex gap-4">
              <Dropdown
                optionLabel="value"
                placeholder="Select Status"
                options={STATUS_LIST}
                name="status"
                value={filterObj?.status}
                optionValue="id"
                className="border border-gray-300 rounded-md w-full"
                onChange={handleFilterChange}
              />
              <button className="w-32 h-9 bg-[#004172] text-white rounded-full" onClick={clearFilter}>
                Clear
              </button>
            </div>
          </div>
        )}

        <div
          className="w-[99%] mt-8 rounded-[5px]"
          style={{ boxShadow: '0px 8px 25px 0px rgba(0, 0, 0, 0.06)' }}
        >
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={<span style={{ fontWeight: 'bold' }}>New</span>} />
            <Tab
              disabled={!shouldShowAssignTab}
              label={
                <span style={{ fontWeight: 'bold' }}>
                  {shouldShowAssignTab ? 'Assign To Me' : ''}
                </span>
              }
            />
            <Tab label={<span style={{ fontWeight: 'bold' }}>ALL</span>} />
          </Tabs>

          <div className="w-full">
            {activeTab === 0 && (
              <NewTabTable
                {...tableProps}
                roleCode={roleCode}
                getConciergeStatusText={getConciergeStatusText}
                onEnquiryLog={(id) => { setEnquiryId(id); setViewServiceEnquiry(true); }}
              />
            )}
            {activeTab === 1 && (
              <AssignTabTable
                {...tableProps}
                getConciergeStyle={getConciergeStyle}
              />
            )}
            {activeTab === 2 && (
              <AllTabTable
                {...tableProps}
                roleCode={roleCode}
                getConciergeStyle={getConciergeStyle}
                getBillingStyle={getBillingStyle}
                getBillingStatusText={getBillingStatusText}
                onEnquiryLog={(id) => { setEnquiryId(id); setViewServiceEnquiry(true); }}
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <Pagination count={count} page={page} color="secondary" onChange={handlePageChange} />
            </Box>
          </div>
        </div>
      </div>

      <BillingView
        visible={billingView}
        billingData={billingData}
        getBillingStyle={getBillingStyle}
        getBillingStatusText={getBillingStatusText}
        onHide={() => { setBillingMode(''); setBillingView(false); }}
      />
    </OurLayouts>
  );
};

export default EnquiryList;